import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { logger } from '@/utils/logger/logger';
import {
  appendCheckpoint,
  cancelTracking,
  finishTracking,
  getActiveTrackings,
  recordSessionFlowIntent,
  startTracking,
  TrackingKey,
} from '@heidi/runtime/performance';

const RECORDING_SESSION_ID_METADATA = 'recording_session_id';
const RECORDING_ATTEMPT_KEYS = [
  TrackingKey.SCRIBE_START_RECORDING,
  TrackingKey.SCRIBE_RESUME_RECORDING,
] as const;

export type RecordingAttemptTrackingKey = (typeof RECORDING_ATTEMPT_KEYS)[number];

export type RecordingAttemptTracking = {
  trackingKey: RecordingAttemptTrackingKey;
  trackingInstanceId?: number | null;
};

export type RecordingStartOptions = {
  shouldBypassConsentCheck?: boolean;
  tracking?: RecordingAttemptTracking;
};

export const recordingStartTrackingMetadata = (
  sessionId: string
): Record<string, unknown> => ({
  [RECORDING_SESSION_ID_METADATA]: sessionId,
});

/**
 * Opens the start or resume attempt of one accepted press and emits its intent.
 * A retry closes the attempt still open as cancelled. Telemetry faults are
 * logged, not thrown; the press keeps whatever attempt it did open.
 */
export const openRecordingAttempt = (
  trackingKey: RecordingAttemptTrackingKey,
  sessionId: string
): RecordingAttemptTracking => {
  let trackingInstanceId: RecordingAttemptTracking['trackingInstanceId'] = null;
  try {
    if (getActiveTrackings().has(trackingKey)) {
      finishTracking({
        key: trackingKey,
        outcome: 'failed',
        error: createSessionFlowPreflightError(
          'StartRecordingCancelled',
          'Recording attempt superseded by an explicit retry'
        ),
        metadata: { preflight_gate: 'superseded_by_retry' },
      });
    }
    startTracking({
      key: trackingKey,
      metadata: recordingStartTrackingMetadata(sessionId),
    });
    trackingInstanceId = getActiveTrackings().get(trackingKey)?.instanceId ?? null;
    // After the open, so no intent lacks an attempt.
    recordSessionFlowIntent(trackingKey);
    lastPressAttemptByKey.set(trackingKey, {
      sessionId,
      trackingInstanceId,
      openedAt: Date.now(),
    });
    return { trackingKey, trackingInstanceId };
  } catch (err) {
    logger.warn('[recording-start] attempt telemetry failed', {
      trackingKey,
      errorName: err instanceof Error ? err.name : typeof err,
    });
    return { trackingKey, trackingInstanceId };
  }
};

// Matches the desktop start debounce, which drops a second call this soon.
const DUPLICATE_PRESS_WINDOW_MS = 1000;

// One slot per key, so a press under the other key cannot hide a repeat.
const lastPressAttemptByKey = new Map<
  RecordingAttemptTrackingKey,
  {
    sessionId: string;
    trackingInstanceId: RecordingAttemptTracking['trackingInstanceId'];
    openedAt: number;
  }
>();

/**
 * `openRecordingAttempt` for a surface with no debounce of its own. Returns
 * null for a repeat press on the same session while the first one's attempt
 * is still fresh and open; the caller drops that press.
 */
export const openRecordingAttemptForPress = (
  trackingKey: RecordingAttemptTrackingKey,
  sessionId: string
): RecordingAttemptTracking | null => {
  const last = lastPressAttemptByKey.get(trackingKey);
  if (
    last?.sessionId === sessionId &&
    last.trackingInstanceId != null &&
    Date.now() - last.openedAt < DUPLICATE_PRESS_WINDOW_MS &&
    getActiveTrackings().get(trackingKey)?.instanceId === last.trackingInstanceId
  ) {
    return null;
  }
  return openRecordingAttempt(trackingKey, sessionId);
};

const isTrackingOwnedBySession = (
  key: RecordingAttemptTrackingKey,
  sessionId: string
): boolean =>
  getActiveTrackings().get(key)?.metadata?.[RECORDING_SESSION_ID_METADATA] === sessionId;

/** The attempt this session already opened, start or resume. */
export const recordingAttemptOwnedBySession = (
  sessionId: string
): RecordingAttemptTracking | undefined => {
  for (const trackingKey of RECORDING_ATTEMPT_KEYS) {
    const entry = getActiveTrackings().get(trackingKey);
    if (entry?.metadata?.[RECORDING_SESSION_ID_METADATA] !== sessionId) continue;
    return { trackingKey, trackingInstanceId: entry.instanceId };
  }
  return undefined;
};

export const ownsRecordingAttempt = ({
  trackingKey,
  trackingInstanceId,
}: RecordingAttemptTracking): boolean =>
  trackingInstanceId === undefined ||
  (trackingInstanceId !== null &&
    getActiveTrackings().get(trackingKey)?.instanceId === trackingInstanceId);

export const appendRecordingAttemptCheckpoint = (
  tracking: RecordingAttemptTracking,
  checkpointName: string,
  metadata?: Record<string, unknown>
): void => {
  if (!ownsRecordingAttempt(tracking)) {
    // A retry replaced this instance. The old callback still reaches dispatch.
    // Log that, and leave the capture path unchanged.
    if (
      checkpointName === 'mutation_dispatched' &&
      typeof tracking.trackingInstanceId === 'number'
    ) {
      logger.warn('[recording-start] superseded attempt continued after retry');
    }
    return;
  }
  appendCheckpoint({ key: tracking.trackingKey, checkpointName, metadata });
};

export const finishRecordingAttempt = (
  tracking: RecordingAttemptTracking,
  params: {
    outcome?: 'failed';
    error?: unknown;
    metadata?: Record<string, unknown>;
  } = {}
): void => {
  if (!ownsRecordingAttempt(tracking)) return;
  if (params.outcome === 'failed') {
    finishTracking({
      key: tracking.trackingKey,
      outcome: 'failed',
      error: params.error,
      metadata: params.metadata,
    });
    return;
  }
  finishTracking({ key: tracking.trackingKey, metadata: params.metadata });
};

export const cancelRecordingAttempt = (tracking: RecordingAttemptTracking): void => {
  if (!ownsRecordingAttempt(tracking)) return;
  cancelTracking({ key: tracking.trackingKey });
};

export const cancelRecordingStartTrackingForSession = ({
  sessionId,
  gate,
  reason,
}: {
  sessionId: string;
  gate: 'consent' | 'shadow_heidi';
  reason: string;
}): boolean => {
  const ownedKeys = RECORDING_ATTEMPT_KEYS.filter((key) =>
    isTrackingOwnedBySession(key, sessionId)
  );

  for (const key of ownedKeys) {
    finishTracking({
      key,
      outcome: 'failed',
      error: createSessionFlowPreflightError('StartRecordingCancelled', reason),
      metadata: { preflight_gate: gate },
    });
  }
  return ownedKeys.length > 0;
};

// The attempt whose capture-engine start is running. The engine reports through
// a key-less port, so only this binding decides which attempt it may touch.
let engineRecordingAttempt: RecordingAttemptTracking | undefined;

export const bindEngineRecordingAttempt = (
  tracking: RecordingAttemptTracking
): (() => void) => {
  engineRecordingAttempt = tracking;
  return () => {
    if (engineRecordingAttempt === tracking) engineRecordingAttempt = undefined;
  };
};

export const appendEngineRecordingAttemptCheckpoint = (
  checkpointName: string,
  metadata?: Record<string, unknown>
): void => {
  if (engineRecordingAttempt) {
    appendRecordingAttemptCheckpoint(engineRecordingAttempt, checkpointName, metadata);
  }
};

export const finishEngineRecordingAttemptFailure = (error: unknown): void => {
  if (engineRecordingAttempt) {
    finishRecordingAttempt(engineRecordingAttempt, { outcome: 'failed', error });
  }
};

export const cancelEngineRecordingAttempt = (): void => {
  if (engineRecordingAttempt) cancelRecordingAttempt(engineRecordingAttempt);
};
